import React, { useState, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lock, Heart, User, CalendarDays, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/auth';
import { useFavorites } from '@/hooks/useFavorites';
import { useProfileDetail } from '@/hooks/useProfileDetail';
import { useToast } from '@/hooks/use-toast';
import { useConversations } from '@/hooks/useConversations';
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileDetailSkeleton from '@/components/profile/ProfileDetailSkeleton';
import ProfileDetailError from '@/components/profile/ProfileDetailError';
import ProfileActionButtons from '@/components/profile/ProfileActionButtons';
import BookingModal from '@/components/profile/BookingModal';
import ProfileImageGallery from '@/components/profile/ProfileImageGallery';
import { ProfileDetailsSection } from '@/components/profile/ProfileDetailsSection.tsx';
import PrivateContentSection from '@/components/profile/sections/ProfilePrivateContent.tsx';
import { Card, CardContent } from '@/components/ui/card';
import { Languages as LanguagesIcon } from 'lucide-react';
import { EscortProfile } from '@/types';

const ProfileDetail = () => {
  const { id: profileId } = useParams<{ id: string }>();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'private_access'>('about');
  const { t, i18n } = useTranslation(['common', 'profile']);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { favorites, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const { startConversation } = useConversations();
  
  const { 
    profile, 
    isLoading, 
    error, 
    profileImage, 
    isGeneratingImage 
  } = useProfileDetail(profileId);

  // Helper pour construire l'URL avec la langue actuelle (remplace getUrlWithLanguage)
  const getUrlWithLanguage = (path: string) => `/${i18n.language}${path}`;

  // Vérifier si l'utilisateur a un accès privé
  const hasPrivateAccess = !!user; 

  const handleBack = () => {
    navigate(getUrlWithLanguage('/explore'));
  };

  const isFavorite = profile ? favorites.has(profile.id) : false;

  const handleToggleFavoriteLocal = (profileId: string) => {
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_favorite'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    toggleFavorite(profileId);
  };

  const handleOpenBookingModal = () => {
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_book'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    setIsBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
  };

  const handleSendMessage = async () => {
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_message'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    
    if (!profileId) return;
    
    try {
      // Start or get conversation (returns the full Conversation object)
      const conversation = await startConversation(profileId);
      if (conversation) {
        // Corrected navigation target to /messages/:conversationId
        console.log(`Navigating from ProfileDetail to /messages/${conversation.id}`);
        navigate(getUrlWithLanguage(`/messages/${conversation.id}`));
      } else {
        // Handle case where conversation could not be started (should be handled by hook's toast)
        console.error('Failed to start conversation from ProfileDetail');
      }
    } catch (error) {
      console.error('Error starting conversation from ProfileDetail:', error);
      toast({
        title: t('common.error'),
        description: t('common.error_starting_conversation'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <ProfileDetailSkeleton onBack={handleBack} />
      </Layout>
    );
  }

  if (error || !profile) {
    return (
      <Layout>
        <ProfileDetailError error={error} onBack={handleBack} />
      </Layout>
    );
  }

  // Préparer les images pour la galerie
  const galleryImages = profile.photos?.gallery || [];
  
  // Obtenir le texte de la bio (gérer l'objet bio correctement)
  const bioText = profile.bio && typeof profile.bio === 'object' 
    ? profile.bio.full || profile.bio.short || "Aucune description disponible."
    : typeof profile.bio === 'string' 
      ? profile.bio 
      : "Aucune description disponible.";

  return (
    <Layout>
      <Suspense fallback={<div>Chargement du profil...</div>}>
        <div className="py-8 px-4 mb-20 md:mb-0 bg-[#fff5f8]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-6">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack} 
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('profile.back')}
              </Button>
            </div>
            
            {/* Header with avatar and basic info */}
            <ProfileHeader 
              profile={profile}
              profileImage={profileImage} 
              isGeneratingImage={isGeneratingImage}
            />
            
            {/* Tab Bar avec bordure blanche comme demandé */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 overflow-hidden">
              <div className="flex">
                <button
                  className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                    activeTab === 'about' 
                      ? 'bg-pink-50 text-pink-600 border-b-2 border-pink-500' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('about')}
                >
                  {t('profile.tabs.about')}
                </button>
                <button
                  className={`flex-1 py-3 px-4 text-center font-medium transition-colors flex items-center justify-center ${
                    activeTab === 'private_access' 
                      ? 'bg-pink-50 text-pink-600 border-b-2 border-pink-500' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('private_access')}
                >
                  <Lock className="w-4 h-4 mr-1" />
                  {t('profile.private_access')}
                </button>
              </div>
            </div>
            
            {/* Main Content - Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Left Column - Image Gallery */}
              <div className="md:col-span-4">
                <ProfileImageGallery 
                  mainImage={profileImage || ''}
                  galleryImages={galleryImages}
                  profileName={profile.displayName}
                />
              </div>
              
              {/* Right Column - Profile Details */}
              <div className="md:col-span-8 space-y-6">
                {/* Tab Content */}
                {activeTab === 'about' && (
                  <ProfileDetailsSection 
                    profile={profile} 
                    canSeeFullProfile={!!user} 
                  />
                )}
                
                {activeTab === 'private_access' && (
                  <PrivateContentSection 
                    profile={profile} 
                    hasAccess={hasPrivateAccess}
                    onSubscribe={() => {
                      if (!user) {
                        toast({
                          title: t('common.login_required'),
                          description: t('profile.login_to_subscribe'),
                          variant: 'default',
                        });
                        navigate(getUrlWithLanguage('/login'));
                      } else {
                        toast({
                          title: 'Fonctionnalité à venir',
                          description: 'La souscription à l\'accès privé sera bientôt disponible.',
                        });
                      }
                    }}
                  />
                )}
              </div>
            </div>
            
            {/* Action Buttons Bar - Toujours visible en bas */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20 shadow-lg md:flex justify-center hidden">
              <div className="max-w-7xl w-full flex justify-between">
                <Button 
                  variant="outline"
                  className="bg-white border border-pink-200 text-pink-600 hover:bg-pink-50"
                  onClick={() => handleToggleFavoriteLocal(profile.id)}
                >
                  <Heart className="h-4 w-4 mr-2" fill={isFavorite ? "currentColor" : "none"} />
                  {t('profile.favorite')}
                </Button>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    className="bg-white border border-pink-200 text-pink-600 hover:bg-pink-50"
                    onClick={handleSendMessage}
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {t('profile.message')}
                  </Button>
                  
                  <Button 
                    className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700"
                    onClick={handleOpenBookingModal}
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {t('profile.booking')}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Mobile Action Buttons */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20 shadow-lg md:hidden">
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => handleToggleFavoriteLocal(profile.id)}
                >
                  <Heart className="h-4 w-4 mr-2" fill={isFavorite ? "currentColor" : "none"} />
                  {t('profile.favorite')}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleSendMessage}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  {t('profile.message')}
                </Button>
                
                <Button 
                  className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600"
                  onClick={handleOpenBookingModal}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  {t('profile.booking')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
      
      {profile && (
        <BookingModal 
          isOpen={isBookingModalOpen}
          onClose={handleCloseBookingModal}
          profile={profile}
        />
      )}
    </Layout>
  );
};

// Flag mapping for common languages
const languageFlags: Record<string, string> = {
  'Français': '🇫🇷',
  'Anglais': '🇬🇧',
  'English': '🇬🇧',
  'Espagnol': '🇪🇸',
  'Spanish': '🇪🇸',
  'Italiano': '🇮🇹',
  'Italien': '🇮🇹',
  'Italian': '🇮🇹',
  'Deutsch': '🇩🇪',
  'Allemand': '🇩🇪',
  'German': '🇩🇪',
  'Russe': '🇷🇺',
  'Russian': '🇷🇺',
  'Japonais': '🇯🇵',
  'Japanese': '🇯🇵',
  'Chinois': '🇨🇳',
  'Chinese': '🇨🇳',
  'Arabe': '🇦🇪',
  'Arabic': '🇦🇪',
  'Portugais': '🇵🇹',
  'Portuguese': '🇵🇹'
};

// Language proficiency stars renderer
const renderLanguageStars = (level: number) => {
  return (
    <div className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg 
          key={i} 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill={i < level ? "currentColor" : "none"} 
          stroke={i < level ? "none" : "currentColor"}
          className={`w-4 h-4 ${i < level ? "text-amber-400" : "text-gray-300"}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ))}
    </div>
  );
};

export const LanguagesSection: React.FC<LanguagesSectionProps> = ({ profile, t }) => {
  // Handling multiple formats for languages data
  let languages = [];
  
  // Format 1: New format - languages array from SQL database
  if (profile.languages && Array.isArray(profile.languages)) {
    languages = profile.languages;
  }
  // Format 2: Old format - in attributes
  else if (profile.attributes?.languages && Array.isArray(profile.attributes.languages)) {
    languages = profile.attributes.languages;
  }
  
  if (languages.length === 0) {
    return null; // Don't render section if no languages
  }

  return (
    <Card className="mb-6 border-pink-100">
      <CardContent className="p-0">
        <div className="bg-pink-50 py-3 px-6 border-b border-pink-100">
          <h3 className="text-lg font-medium text-pink-700 flex items-center">
            <LanguagesIcon size={18} className="mr-2 text-pink-500" />
            {t('profile.languages')}
          </h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {languages.map((language, index) => {
              // Handle different possible formats
              const languageName = typeof language === 'string' ? language : language.name;
              const languageLevel = typeof language === 'object' ? 
                (language.level || 5) : 5; // Default level if only string provided
              
              // Get appropriate flag or use a generic one
              const flag = languageFlags[languageName] || '🌐';

              return (
                <div key={index} className="flex justify-between items-center">
                  <span className="font-medium">
                    {flag} {languageName}
                  </span>
                  {renderLanguageStars(languageLevel)}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileDetail;
